import type { UserProfile } from '@molecule/app-auth'
import { getProvider as getI18nProvider, registerContent } from '@molecule/app-i18n'

export const API_BASE_URL = import.meta.env?.VITE_API_URL || '/api'

export const authConfig = {
  baseURL: API_BASE_URL,
  loginEndpoint: '/users/log-in',
  registerEndpoint: '/users',
  forgotPasswordEndpoint: '/users/forgot-password',
  // Consume the reset token (POST). Default is '/auth/password/reset/confirm',
  // which the API never served — so reset links were dead. Points at the now-
  // mounted POST /users/reset-password.
  resetPasswordEndpoint: '/users/reset-password',
  // '/users/me/password' (authSelf rewrites `me`→userId), matching the mounted
  // POST alias. The old '/users/password' had no matching route → 404.
  changePasswordEndpoint: '/users/me/password',
  // [M1-1] storage: 'memory' EXPLICITLY — @molecule/app-auth's browser default is
  // Web Storage, and a localStorage bearer copy is XSS-exfiltratable and defeats the
  // httpOnly session cookie. The session persists across reloads via cookie restore:
  // initialize() probes currentUserEndpoint (GET /users/me) with credentials and
  // re-establishes the session from the httpOnly cookie — so the token never touches
  // JavaScript.
  storage: 'memory' as const,
  currentUserEndpoint: '/users/me',
  // A GUEST session must never hydrate as a signed-in client: guests hold an
  // httpOnly session cookie (their API calls authenticate fine), and if the
  // `mol_auth` hint ever exists for one (e.g. minted by an old API's token
  // auto-refresh), the cookie-restore probe would return the anonymous user row
  // and flip every `!user` guest check in the app. Rejecting the restore keeps
  // the client logged out and clears the stale hint.
  // (`isAnonymous` is a molecule-dev users column, not part of the shared
  // UserProfile type — same inline-cast pattern as planKey in SettingsPanel.)
  shouldRestoreUser: (user: UserProfile) =>
    (user as UserProfile & { isAnonymous?: boolean }).isAnonymous !== true,
  autoRefresh: true,
}

export const oauthConfig = {
  baseURL: API_BASE_URL,
  oauthProviders: ['github', 'gitlab', 'google', 'twitter'],
  oauthEndpoint: '/users/oauth',
}

/**
 * Long-form LEGAL docs served in English for every locale, for now.
 *
 * The corrected privacy/terms wording lives in the `en` modules; the ~73 non-English
 * `privacyPolicy.ts`/`termsOfService.ts` still carry the pre-correction copy (incl. the
 * now-false "we do not track you in any way" absolute), and machine-translating legal
 * text is unsafe. Decision (2026-06-15): serve the corrected English to all users until
 * the docs are professionally localized + lawyer-reviewed (parked-decisions PRD10/D4).
 *
 * Those 146 unreachable modules are parked in `locales/.pending-legal/` — the leading
 * dot keeps them out of the `./locales/${loc}/${module}.ts` glob below, which otherwise
 * expands to a wildcard over every locale directory and bundled all 146 of them:
 * ~824 kB of lazy chunks that could never load, every one precached by the service
 * worker in the PWA build and publicly fetchable on the deployed site.
 * To re-enable per-locale legal content later, restore the modules per
 * `locales/.pending-legal/README.md` FIRST, then drop the module from this set — the
 * set is all-or-nothing per module, so an unrestored locale becomes a runtime load
 * failure rather than an English fallback.
 */
const ENGLISH_ONLY_CONTENT = new Set(['privacyPolicy', 'termsOfService', 'dataProcessingAddendum'])

/**
 * Lazily load a locale content module and register it for automatic
 * reload on locale changes. The first call loads the content and registers
 * its loader; future locale changes reload it automatically.
 */
export async function loadContent(module: string): Promise<void> {
  const provider = getI18nProvider()
  const locale = provider.getLocale()
  const loader = async (loc: string): Promise<void> => {
    // Legal docs always pull from `en` (see ENGLISH_ONLY_CONTENT), but register under the
    // ACTIVE locale so the page renders the corrected English regardless of UI language.
    const sourceLoc = ENGLISH_ONLY_CONTENT.has(module) ? 'en' : loc
    const content = await import(`./locales/${sourceLoc}/${module}.ts`)
    provider.addTranslations(loc, content[module])
  }
  registerContent(module, loader)
  await loader(locale)
}
